import React from 'react';

export interface PricingTableProps {
  onUpgradeClick: () => void;
  compact?: boolean;
}

export const PricingTable: React.FC<PricingTableProps> = ({
  onUpgradeClick,
  compact = false,
}) => {
  const features = [
    {
      name: 'Daily Sessions',
      free: '3 sessions',
      premium: 'Unlimited',
      icon: '💬',
    },
    {
      name: 'Session Duration',
      free: '10 minutes',
      premium: 'Unlimited',
      icon: '⏱️',
    },
    {
      name: 'Global Matching',
      free: 'Limited countries',
      premium: 'Match worldwide',
      icon: '🌍',
    },
    {
      name: 'Skip to Next Match',
      free: '❌',
      premium: '✅',
      icon: '⏭️',
    },
    {
      name: 'Priority Matching',
      free: '❌',
      premium: '✅',
      icon: '🚀',
    },
    {
      name: 'Queue Wait Time',
      free: '30 seconds',
      premium: 'Instant',
      icon: '⚡',
    },
    {
      name: 'Conversation History',
      free: '❌',
      premium: '✅ (Coming Soon)',
      icon: '📝',
    },
    {
      name: 'Advanced Filters',
      free: '❌',
      premium: '✅ (Coming Soon)',
      icon: '🔍',
    },
  ];

  return (
    <div style={styles.container}>
      {!compact && (
        <div style={styles.header}>
          <h2 style={styles.title}>Choose Your Plan</h2>
          <p style={styles.subtitle}>
            Upgrade to Premium for unlimited connections and exclusive features
          </p>
        </div>
      )}

      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead>
            <tr style={styles.headerRow}>
              <th style={styles.featureHeader}>Feature</th>
              <th style={styles.planHeader}>
                <div style={styles.planHeaderContent}>
                  <span style={styles.planName}>Free</span>
                  <span style={styles.planPrice}>$0</span>
                </div>
              </th>
              <th style={{ ...styles.planHeader, ...styles.premiumHeader }}>
                <div style={styles.planHeaderContent}>
                  <span style={styles.planName}>Premium ⭐</span>
                  <span style={styles.planPrice}>
                    $9.99<span style={styles.priceUnit}>/month</span>
                  </span>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {features.map((feature, index) => (
              <tr
                key={index}
                style={{
                  ...styles.featureRow,
                  ...(index % 2 === 0 ? styles.featureRowEven : {}),
                }}
              >
                <td style={styles.featureCell}>
                  <span style={styles.featureIcon}>{feature.icon}</span>
                  <span style={styles.featureName}>{feature.name}</span>
                </td>
                <td style={styles.valueCell}>{feature.free}</td>
                <td style={{ ...styles.valueCell, ...styles.premiumCell }}>
                  {feature.premium}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={styles.ctaContainer}>
        <button onClick={onUpgradeClick} style={styles.upgradeButton}>
          ⭐ Upgrade to Premium Now
        </button>
        <p style={styles.ctaNote}>Cancel anytime • No commitment • Instant access</p>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    width: '100%',
    maxWidth: '900px',
    margin: '0 auto',
  },
  header: {
    textAlign: 'center',
    marginBottom: '2rem',
  },
  title: {
    fontSize: '2.5rem',
    fontWeight: '700',
    color: 'var(--color-deep-purple)',
    marginBottom: '0.5rem',
  },
  subtitle: {
    fontSize: '1.1rem',
    color: 'var(--color-gray-600)',
    margin: 0,
  },
  tableContainer: {
    backgroundColor: 'white',
    borderRadius: 'var(--radius-lg)',
    overflow: 'hidden',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
    marginBottom: '2rem',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  headerRow: {
    backgroundColor: 'var(--color-gray-100)',
  },
  featureHeader: {
    padding: '1.5rem 1.5rem',
    textAlign: 'left',
    fontSize: '1.1rem',
    fontWeight: '600',
    color: 'var(--color-gray-700)',
    borderBottom: '2px solid var(--color-gray-300)',
  },
  planHeader: {
    padding: '1.5rem 1.5rem',
    textAlign: 'center',
    borderBottom: '2px solid var(--color-gray-300)',
    minWidth: '180px',
  },
  premiumHeader: {
    background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1), rgba(217, 119, 6, 0.1))',
    borderLeft: '3px solid #f59e0b',
  },
  planHeaderContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '0.5rem',
  },
  planName: {
    fontSize: '1.1rem',
    fontWeight: '700',
    color: 'var(--color-deep-purple)',
  },
  planPrice: {
    fontSize: '1.8rem',
    fontWeight: '700',
    color: 'var(--color-warm-orange)',
  },
  priceUnit: {
    fontSize: '1rem',
    color: 'var(--color-gray-600)',
    fontWeight: '500',
  },
  featureRow: {
    borderBottom: '1px solid var(--color-gray-200)',
    transition: 'background-color 0.2s ease',
  },
  featureRowEven: {
    backgroundColor: 'var(--color-gray-50)',
  },
  featureCell: {
    padding: '1rem 1.5rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
  },
  featureIcon: {
    fontSize: '1.3rem',
    flexShrink: 0,
  },
  featureName: {
    fontSize: '1rem',
    fontWeight: '500',
    color: 'var(--color-gray-800)',
  },
  valueCell: {
    padding: '1rem 1.5rem',
    textAlign: 'center',
    fontSize: '0.95rem',
    color: 'var(--color-gray-700)',
    fontWeight: '500',
  },
  premiumCell: {
    borderLeft: '3px solid #f59e0b',
    backgroundColor: 'rgba(245, 158, 11, 0.05)',
    color: 'var(--color-warm-orange)',
    fontWeight: '600',
  },
  ctaContainer: {
    textAlign: 'center',
  },
  upgradeButton: {
    padding: '1rem 3rem',
    fontSize: '1.2rem',
    fontWeight: '700',
    color: 'white',
    background: 'linear-gradient(135deg, var(--color-warm-orange), #d97706)',
    border: 'none',
    borderRadius: 'var(--radius-lg)',
    cursor: 'pointer',
    boxShadow: '0 6px 20px rgba(245, 158, 11, 0.4)',
    transition: 'all 0.3s ease',
    marginBottom: '1rem',
  },
  ctaNote: {
    fontSize: '0.9rem',
    color: 'var(--color-gray-600)',
    margin: 0,
  },
};
